import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { ContactUsReason } from 'prismas';
import { Fragment } from 'react';
import { classes } from 'shared';

const tReasons = [
  { id: ContactUsReason.FINANCIAL_DONATION, display: 'Financial Donation' },
  { id: ContactUsReason.FURNITURE_DONATION, display: 'Furniture Donation' },
  { id: ContactUsReason.VOLUNTEERING, display: 'Volunteering' },
  { id: ContactUsReason.REFERRAL_PARTNER, display: 'Referral Partner' },
  { id: ContactUsReason.OTHER, display: 'Other' },
];

function genReasons(placeholder = { id: null, display: 'Select a Contact Reason' }) {
  return [placeholder, ...tReasons];
}

export function ContactReasonSelect({
  reason,
  onChange,
  error,
  onChangeError,
  disabled,
  placeholder,
}: {
  reason: ContactUsReason;
  onChange: (reason: ContactUsReason) => void;
  error: string | string[] | undefined;
  onChangeError: (error: string | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const reasons = genReasons(placeholder && { id: null, display: placeholder });
  const selected = reasons.find(r => r.id === reason) || reasons[0];

  return (
    <>
      <Listbox value={selected} onChange={updateReason} disabled={disabled}>
        <div className="relative mt-[8px]">
          <label
            className={classes(
              'absolute -top-2 left-2 z-[1] -mt-px inline-block bg-white px-1 text-xs font-medium text-gray-900',
              placeholder && 'hidden',
            )}>
            Contact Reason
          </label>
          <Listbox.Button
            className={classes(
              'relative block w-full cursor-pointer rounded-md border bg-white px-4 py-3 pl-3 pr-10 text-left placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 disabled:cursor-default disabled:bg-gray-200 disabled:text-gray-600',
              getError()
                ? 'border-red-300 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
            )}>
            <span className="block truncate">{selected.display}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </span>
          </Listbox.Button>

          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Listbox.Options className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {reasons.map(person => (
                <Listbox.Option
                  key={person.id}
                  className={({ active }) =>
                    classes(
                      active ? 'bg-blue-600 text-white' : 'text-gray-900',
                      'relative cursor-pointer select-none py-2 pl-8 pr-4',
                    )
                  }
                  value={person}>
                  {({ selected, active }) => (
                    <>
                      <span className={classes(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                        {person.display}
                      </span>

                      {selected ? (
                        <span
                          className={classes(
                            active ? 'text-white' : 'text-blue-600',
                            'absolute inset-y-0 left-0 flex items-center pl-1.5',
                          )}>
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      <span className="text-sm text-red-500 empty:hidden">{getError()}</span>
    </>
  );

  function updateReason(reason: (typeof reasons)[0]) {
    if (reason.id === null) onChangeError('Please select a reason');
    onChange(reason.id);
  }

  function getError() {
    if (!error) return;
    if (typeof error !== 'string') return error?.[0];
    return error;
  }
}
